/**
 * Wallet — ввод адреса кошелька TRC20 (шаг Flow step="wallet", первый после «Купить»).
 * Валидация TRC20, вставка из буфера, префилл последнего адреса. Пропсы: onNext(address), onBack, initial?.
 */
import { useCallback, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { tg } from "@/telegram/tg";
import { FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { Field } from "@/components/Field";
import { validateTrc20 } from "@/lib/format";

type Props = { onNext: (address: string) => void; onBack: () => void; initial?: string };

export function Wallet({ onNext, onBack, initial }: Props) {
  const [addr, setAddr] = useState(initial ?? "");
  const [touched, setTouched] = useState(false);
  const valid = validateTrc20(addr);
  const err = touched && addr && !valid ? "Адрес не похож на TRC20" : undefined;

  const submit = useCallback(() => {
    if (!valid) return;
    onNext(addr.trim());
  }, [valid, addr, onNext]);

  useBackButton(onBack);
  useMainButton({ text: "Продолжить", onClick: submit, active: valid });

  const applyPasted = (t: string | null) => {
    if (t) {
      setAddr(t.trim());
      setTouched(true);
    }
  };

  const paste = async () => {
    // В webview Telegram navigator.clipboard.readText часто заблокирован —
    // используем нативный метод Telegram, с фолбэком на обычный clipboard.
    const wa = tg();
    if (wa?.readTextFromClipboard) {
      wa.readTextFromClipboard(applyPasted);
      return;
    }
    try {
      applyPasted(await navigator.clipboard.readText());
    } catch {
      /* clipboard недоступен */
    }
  };

  return (
    <Screen>
      <Hero
        icon="👛"
        title="Куда отправить USD"
        subtitle="Адрес кошелька TRC20. Проверьте, что сеть именно TRC20 — иначе средства будут потеряны."
      />

      <Field
        label="TRC20-адрес"
        placeholder="T..."
        value={addr}
        onChange={(e) => {
          setAddr(e.target.value);
          setTouched(true);
        }}
        onBlur={() => setTouched(true)}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        suffix={
          <button onClick={paste} className="text-[13px] font-medium text-brand active:opacity-70">
            Вставить
          </button>
        }
        error={err}
        hint={!err ? "Начинается на T, длина 34 символа" : undefined}
      />

      <Note variant="danger" className="mt-5">
        ⚠️ Отправка в другую сеть (ERC20, BEP20) приведёт к потере средств. Дважды проверьте адрес.
      </Note>

      <FallbackButton text="Продолжить" onClick={submit} disabled={!valid} />
    </Screen>
  );
}
